import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { controlToastState, Modal, Select, TextInput } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { FormConfigModel } from "../../../data-model/formConfigModel";
import { FormField } from "../../../data-model/pharmaPaFormModel";
import { fetchSingleMedicalPaFormConfig } from "../../../redux/slice/medicalPa/singleMedicalPaFormConfig";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";

interface FormConfigModalProps {
  onClose: () => void;
  existingData?: {
    formTitle: string;
    formId: string;
    drugName: string;
    providerName: string;
    fields: FormField[];
  };
  isEditing?: boolean;
}

const FormConfigModal = ({
  onClose,
  existingData,
  isEditing,
}: FormConfigModalProps) => {
  const [formName, setFormName] = useState("");
  const [formId, setFormId] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedFormTemplate, setSelectedFormTemplate] =
    useState<string>("none");

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { formConfigs } = useSelector((state: RootState) => state.formConfig);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (existingData) {
      setFormName(existingData?.formTitle || "");
      setSelectedDrug(existingData?.drugName || "");
      setSelectedProvider(existingData?.providerName || "");
      setFormId(existingData?.formId || "");
    }
  }, [existingData]);

  const formConfigOptions = useMemo(() => {
    let options = [{ value: "none", label: "No Form Selected" }];
    options = options.concat(
      formConfigs.map((config) => ({
        value: config.id,
        label: config.data.formTitle,
      })),
    );
    return options;
  }, [formConfigs]);

  const handleCancel = () => {
    onClose();
  };

  const shouldSaveButtonBeDisabled = useMemo(() => {
    return formName.trim().length === 0 || formId.trim().length === 0;
  }, [formName, formId]);

  const handleSave = async () => {
    setSaving(true);
    const cleanFormId = formId.replace(/"/g, "");

    try {
      if (isEditing && existingData) {
        await FirestoreService.updateDocument(
          FirestoreCollectionReference.medicalPaFormConfig(),
          existingData.formId,
          {
            "data.formTitle": formName,
            "data.drugName": selectedDrug,
            "data.providerName": selectedProvider,
            updated_at: new Date().toISOString(),
            updated_by: user?.email || "N/A",
          },
        );
        controlToastState("form-config-save-success");
        dispatch(fetchSingleMedicalPaFormConfig(existingData.formId));
      } else {
        const templateForm = formConfigs.find(
          (config) => config.id === selectedFormTemplate,
        );

        const newDocument: FormConfigModel = {
          id: cleanFormId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: user?.email || "N/A",
          data: {
            formTitle: formName,
            drugName: selectedDrug,
            providerName: selectedProvider,
            fields: templateForm?.data?.fields || [],
          },
        };

        await FirestoreService.addDocumentWithId(
          FirestoreCollectionReference.medicalPaFormConfig(),
          newDocument,
        );
        controlToastState("form-config-save-success");
      }
    } catch (error) {
      logError(error as Error, "Error saving form config");
      controlToastState("form-config-save-failure");
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <Modal
      dialogId="form-config-modal"
      title="Form Config"
      saveButtonText={saving ? "Saving..." : "Save"}
      cancelText="Cancel"
      onSave={handleSave}
      onCancel={handleCancel}
      onClose={onClose}
      disableSave={shouldSaveButtonBeDisabled || saving}
    >
      <div className="form-config-modal">
        <div className="form-config-modal__header flex flex-col gap-2">
          <TextInput
            id="form-name"
            label="Form Name"
            onChange={(data) => setFormName(data.value)}
            defaultValue={existingData?.formTitle || ""}
            required
            error={formName.trim().length === 0 ? "Form name is required" : ""}
          />
          <TextInput
            id="form-id"
            label="Form ID"
            onChange={(data) => setFormId(data.value)}
            defaultValue={existingData?.formId || ""}
            required
            error={formId.trim().length === 0 ? "Form ID is required" : ""}
            readOnly={!!existingData?.formId}
          />
          <TextInput
            id="drug-name"
            label="Drug Name"
            defaultValue={existingData?.drugName || ""}
            onChange={(data) => setSelectedDrug(data.value)}
          />
          <TextInput
            id="provider-name"
            label="Provider Name"
            defaultValue={existingData?.providerName || ""}
            onChange={(data) => setSelectedProvider(data.value)}
          />
          {existingData ? (
            <></>
          ) : (
            <>
              <Select
                id="form-template"
                label="Form Template"
                options={formConfigOptions}
                onOptionChange={(data) => setSelectedFormTemplate(data.value)}
                defaultValue={"none"}
                placeholder="Select the form to be used as the template"
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FormConfigModal;
